export interface CharacterCard {
  id: string
  name: string
  description: string
  personality: string
  scenario: string
  first_mes: string
  mes_example: string
  creatorcomment: string
  avatar: string
  tags: string[]
  creator: string
  character_version: string
  alternate_greetings: string[]
  system_prompt: string
  post_history_instructions: string
  character_book?: CharacterBook
  extensions: Record<string, unknown>
  created_at: number
  updated_at: number
}

export interface CharacterBook {
  name: string
  description: string
  scan_depth: number
  token_budget: number
  recursive_scanning: boolean
  extensions: Record<string, unknown>
  entries: WorldBookEntry[]
}

export const WORLD_INFO_POSITION = {
  BEFORE_CHAR: 0,
  AFTER_CHAR: 1,
  AN_TOP: 2,
  AN_BOTTOM: 3,
  AT_DEPTH: 4,
  EM_TOP: 5,
  EM_BOTTOM: 6,
  OUTLET: 7,
} as const

export type WorldInfoPositionValue = (typeof WORLD_INFO_POSITION)[keyof typeof WORLD_INFO_POSITION]

export const WORLD_INFO_POSITION_LABELS: Record<WorldInfoPositionValue, string> = {
  [WORLD_INFO_POSITION.BEFORE_CHAR]: 'before_char',
  [WORLD_INFO_POSITION.AFTER_CHAR]: 'after_char',
  [WORLD_INFO_POSITION.AN_TOP]: 'before_AN',
  [WORLD_INFO_POSITION.AN_BOTTOM]: 'after_AN',
  [WORLD_INFO_POSITION.AT_DEPTH]: 'at_depth',
  [WORLD_INFO_POSITION.EM_TOP]: 'before_example',
  [WORLD_INFO_POSITION.EM_BOTTOM]: 'after_example',
  [WORLD_INFO_POSITION.OUTLET]: 'outlet',
}

export interface WorldBookEntry {
  id: number
  keys: string[]
  secondary_keys: string[]
  content: string
  comment: string
  name: string
  enabled: boolean
  disable: boolean
  constant: boolean
  selective: boolean
  selectiveLogic: number
  position: WorldInfoPositionValue
  order: number
  depth: number
  role: number
  case_sensitive: boolean
  matchWholeWords: boolean | null
  priority: number
  probability: number
  useProbability: boolean
  scanDepth: number | null
  excludeRecursion: boolean
  preventRecursion: boolean
  delayUntilRecursion: boolean
  sticky: number | null
  cooldown: number | null
  delay: number | null
  group: string
  groupWeight: number
  ignoreBudget: boolean
  extensions: Record<string, unknown>
  insertion_order: number
}

export interface ParsedCard {
  name: string
  description: string
  personality: string
  scenario: string
  first_mes: string
  mes_example: string
  creatorcomment: string
  tags: string[]
  creator: string
  character_version: string
  alternate_greetings: string[]
  system_prompt: string
  post_history_instructions: string
  character_book?: CharacterBook
  extensions: Record<string, unknown>
  character_uuid?: string
  nickname?: string
  creator_notes?: string
  source?: string[]
  group_only_greetings?: string[]
  entries: WorldBookEntry[]
}

export type PresetType = 'openai' | 'instruct' | 'context' | 'sysprompt' | 'unknown'

export interface OpenAIPresetPrompt {
  identifier: string
  name: string
  role?: string
  content?: string
  system_prompt?: boolean
  marker?: boolean
  enabled?: boolean
  injection_position?: number
  injection_depth?: number
  injection_order?: number
  forbid_overrides?: boolean
}

export interface OpenAIPresetOrder {
  identifier: string
  enabled: boolean
}

export interface ParsedPreset {
  preset_type: PresetType
  name: string
  description: string
  temperature: number
  top_p: number
  top_k: number
  repetition_penalty: number
  min_p: number
  presence_penalty: number
  frequency_penalty: number
  max_tokens: number
  context_length: number
  stop_sequences: string[]
  prompts: OpenAIPresetPrompt[]
  prompt_order: OpenAIPresetOrder[]
  use_world_info: boolean
  use_examples: boolean
  instruct?: InstructPresetData
  context?: ContextPresetData
  sysprompt?: SyspromptPresetData
}

export interface InstructPresetData {
  name: string
  input_sequence: string
  output_sequence: string
  system_sequence: string
  stop_sequence: string
  first_input_sequence: string
  last_input_sequence: string
  first_output_sequence: string
  last_output_sequence: string
  last_system_sequence: string
  input_suffix: string
  output_suffix: string
  system_suffix: string
  wrap: boolean
  macro: boolean
  names_behavior: string | number
  system_same_as_user: boolean
  skip_examples: boolean
  story_string_prefix: string
  story_string_suffix: string
  sequences_as_stop_strings: boolean
}

export interface ContextPresetData {
  name: string
  story_string: string
  example_separator: string
  chat_start: string
  story_string_position: number
  story_string_depth: number
  story_string_role: number
  always_force_name2: boolean
  names_as_stop_strings: boolean
}

export interface SyspromptPresetData {
  name: string
  content: string
  post_history: string
}

export interface PresetPrompt {
  identifier: string
  name: string
  role: string
  content: string
  enabled: boolean
  system_prompt: boolean
  marker: boolean
  injection_position: number
  injection_depth: number
}

export interface Preset {
  id: string
  name: string
  description: string
  temperature: number
  top_p: number
  top_k: number
  repetition_penalty: number
  min_p: number
  presence_penalty: number
  frequency_penalty: number
  max_tokens: number
  context_length: number
  stop_sequences: string[]
  created_at: number
  updated_at: number
}

export interface ChatSession {
  id: string
  character_id: string
  name: string
  messages: ChatMessage[]
  created_at: number
  updated_at: number
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  name?: string
  timestamp: number
  swipes?: string[]
  current_swipe_id?: number
  send_date?: number
  extra?: Record<string, unknown>
}

export interface UserPersona {
  id: string
  name: string
  description: string
  avatar: string
  creator: string
  personality: string
  mes_example: string
  extensions: Record<string, unknown>
  created_at: number
  updated_at: number
}

export interface GroupChat {
  id: string
  name: string
  members: string[]
  avatar: string
  allow_self_responses: boolean
  activated: boolean
  creator: string
  description: string
  chat_metadata: Record<string, unknown>
  created_at: number
  updated_at: number
}

export interface AppSettings {
  theme: 'dark' | 'light' | 'system'
  language: string
  font_size: number
  chat_width: number
  show_timestamps: boolean
  auto_save: boolean
  auto_save_interval: number
  sound_enabled: boolean
  animations_enabled: boolean
}
